<!-- eslint-disable prettier/prettier -->
<script lang="ts">
import { defineComponent, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { marked } from 'marked';
import { defaultBackendLlm } from '@/shared/backend/llm';

export default defineComponent({
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  setup(_, { emit }) {
    const { t } = useI18n();
    const store = useStore();
    const question = ref('');
    const mode = ref<'ask' | 'stream'>('stream');
    let streamAbort: AbortController | null = null;

    const answer = ref('');
    const answerHtml = computed(() =>
      answer.value ? marked.parse(answer.value) : ''
    );
    const loading = ref(false);

    // feedback state
    const selectedQuality = ref<number | null>(null);
    const selectedTypes = ref<number[]>([]);
    const otherSelected = ref(false);
    const feedbackDetails = ref('');
    const feedbackSent = ref(false);
    const feedbackLoading = ref(false);

    function close() {
      emit('update:modelValue', false);
    }

    function resetFeedback() {
      selectedQuality.value = null;
      selectedTypes.value = [];
      otherSelected.value = false;
      feedbackDetails.value = '';
      feedbackSent.value = false;
    }

    function selectQuality(quality: number) {
      selectedQuality.value = quality;
      selectedTypes.value = [];
      otherSelected.value = false;
      feedbackDetails.value = '';
      feedbackSent.value = false;
    }

    async function submitFeedback() {
      if (!selectedQuality.value || feedbackSent.value) return;
      feedbackLoading.value = true;
      try {
        const user = store.getters['auth/getUser'];
        const userName = user
          ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
          : undefined;
        const types =
          selectedTypes.value.length > 0 ? selectedTypes.value : undefined;
        const details = feedbackDetails.value
          ? feedbackDetails.value
          : undefined;

        await defaultBackendLlm.sendFeedback({
          query: question.value,
          answer: answer.value,
          projectId: '7e945301-b0c8-4df8-b5fe-7d851a7a6a46',
          roleId: 'd72febda-9620-40ab-8f72-c77fc5d3cfd3',
          userName: userName || undefined,
          feedbackQuality: selectedQuality.value ?? undefined,
          feedbackTypes: types,
          feedbackDetails: details,
        });
        feedbackSent.value = true;
      } catch (err) {
        console.error('Feedback error', err);
      } finally {
        feedbackLoading.value = false;
      }
    }

    async function send() {
      if (!question.value.trim()) return;
      loading.value = true;
      answer.value = '';
      resetFeedback();
      try {
        const res = await store.dispatch('llmModule/ask', {
          question: question.value,
          role: 'd72febda-9620-40ab-8f72-c77fc5d3cfd3', //all
          project: '7e945301-b0c8-4df8-b5fe-7d851a7a6a46', //attmag
          model: null,
        });
        answer.value = res?.answer ?? '';
      } finally {
        loading.value = false;
      }
    }

    async function testLlm() {
      if (mode.value === 'stream') {
        await testLlmStream();
        return;
      }
      await send();
    }
    async function testLlmStream() {
      if (!question.value.trim()) return;
      if (streamAbort) {
        streamAbort.abort();
      }
      streamAbort = new AbortController();
      answer.value = '';
      resetFeedback();
      loading.value = true;
      try {
        await defaultBackendLlm.streamChat(
          question.value,
          'd72febda-9620-40ab-8f72-c77fc5d3cfd3', //all
          '7e945301-b0c8-4df8-b5fe-7d851a7a6a46', //attmag
          (chunk) => {
            answer.value += chunk;
          },
          streamAbort.signal
        );
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          console.error('Stream error', err);
        }
      } finally {
        loading.value = false;
        streamAbort = null;
      }
    }

    return {
      t,
      close,
      question,
      answer,
      answerHtml,
      loading,
      send,
      mode,
      testLlm,
      selectedQuality,
      selectedTypes,
      otherSelected,
      feedbackDetails,
      feedbackSent,
      feedbackLoading,
      selectQuality,
      submitFeedback,
    };
  },
});
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="800px"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card dense>
      <v-card-title
        class="d-flex align-center justify-space-between bg-primary"
      >
        <span>{{ t('title') }}</span>
        <v-btn icon variant="text" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col
            cols="12"
            md="7"
            :class="$vuetify.display.xs ? 'text-body-medium' : ''"
          >
            <div class="text-body-large font-weight-bold mb-4">
              {{ t('description1') }}
            </div>

            <img
              v-if="$vuetify.display.xs"
              :src="require(`@/assets/disa-chat-picture.png`)"
              width="240"
              round
            />

            <div class="text-body-medium font-weight-bold mb-4">
              {{ t('description2') }}
            </div>
            <div class="text-body-small mb-4">{{ t('description3') }}</div>

            <div class="text-body-medium font-weight-bold mb-4">
              {{ t('description4') }}
            </div>
            <div class="text-body-small mb-4">{{ t('description5') }}</div>
          </v-col>
          <v-col cols="5" v-if="!$vuetify.display.xs">
            <img
              :src="require(`@/assets/disa-chat-picture.png`)"
              width="330"
              round
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="10">
            <v-text-field
              v-model="question"
              :label="t('question')"
              density="comfortable"
              variant="outlined"
              @keyup.enter="testLlm"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="2">
            <v-btn
              :class="$vuetify.display.xs ? 'mt-n8' : ''"
              height="48"
              block
              variant="flat"
              color="primary"
              :loading="loading"
              @click="testLlm"
            >
              {{ t('send') }}
            </v-btn>
          </v-col>
        </v-row>

        <!--  <v-row>
          <v-col cols="12">
            <v-btn-toggle
              v-model="mode"
              mandatory
              density="compact"
              class="mb-2"
            >
              <v-btn value="ask">Ask</v-btn>
              <v-btn value="stream">Stream</v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row> -->

        <div v-if="loading" class="text-title-small mb-2">Answering...</div>
        <div v-if="loading" class="braille-loader wave-style text-primary">
          <span>⡀⣀⣄⣤⣦⣷⣿⣷⣴⣦⣤⣄⣀⡀⡀⣀⣄⣤⣦⣷⣿⣷⣴⣦⣤⣄⣀⡀</span>
          <!-- <span>⠋⠙⠹⠸⠼⠴⠦⠧⠇⠏⠋⠙⠹⠸⠼⠴⠦⠧⠇⠏</span> -->
          <!-- <span>⡆⡇⠇⠃⠁⠉⠘⠸⢸⢸⡆⡇⠇⠃⠁⠉⠘⠸⢸⢸⡆⡇⠇⠃⠁⠉⠘⠸⢸⢸</span> -->
        </div>

        <v-sheet
          v-if="answer"
          variant="outlined"
          rounded
          class="pa-4 mt-2"
          :class="[
            { 'markdown-body': !loading },
            $vuetify.display.xs ? 'text-body-medium' : '',
          ]"
        >
          <div v-if="loading" style="white-space: pre-wrap">{{ answer }}</div>
          <div v-else v-html="answerHtml"></div>
        </v-sheet>

        <!-- feedback section -->
        <v-row v-if="answer && !loading" class="mt-3">
          <v-col cols="7">
            <div class="text-body-medium font-weight-bold mb-2">
              {{ t('feedbackTitle') }}
            </div>
            <div
              :class="
                $vuetify.display.xs
                  ? 'd-flex flex-column ga-2 align-start'
                  : 'd-flex ga-2 align-start'
              "
            >
              <v-btn
                variant="text"
                density="compact"
                :class="{ 'feedback-quality-selected': selectedQuality === 1 }"
                @click="selectQuality(1)"
              >
                <v-icon color="success" size="small" class="mr-1"
                  >mdi-circle</v-icon
                >
                {{ t('feedbackGood') }}
              </v-btn>
              <v-btn
                variant="text"
                density="compact"
                :class="{ 'feedback-quality-selected': selectedQuality === 2 }"
                @click="selectQuality(2)"
              >
                <v-icon color="warning" size="small" class="mr-1"
                  >mdi-circle</v-icon
                >
                {{ t('feedbackSoSo') }}
              </v-btn>
              <v-btn
                variant="text"
                density="compact"
                :class="{ 'feedback-quality-selected': selectedQuality === 3 }"
                @click="selectQuality(3)"
              >
                <v-icon color="error" size="small" class="mr-1"
                  >mdi-circle</v-icon
                >
                {{ t('feedbackBad') }}
              </v-btn>
            </div>

            <div v-if="selectedQuality === 3" class="mt-3">
              <div class="text-body-medium font-weight-bold mb-1">
                {{ t('feedbackWhyTitle') }}
              </div>
              <v-checkbox
                v-model="selectedTypes"
                :value="1"
                :label="t('feedbackAnswerUnclear')"
                density="compact"
                hide-details
                class="mb-1"
              ></v-checkbox>
              <v-checkbox
                v-model="selectedTypes"
                :value="2"
                :label="t('feedbackTooFewDetails')"
                density="compact"
                hide-details
                class="mb-1"
              ></v-checkbox>
              <v-checkbox
                v-model="selectedTypes"
                :value="3"
                :label="t('feedbackNotHelpful')"
                density="compact"
                hide-details
                class="mb-1"
              ></v-checkbox>
              <div class="d-flex align-center gap-2 mt-1">
                <v-checkbox
                  v-model="selectedTypes"
                  :value="4"
                  :label="t('feedbackOther') + ':'"
                  density="compact"
                  hide-details
                ></v-checkbox>
                <v-text-field
                  v-if="selectedTypes.includes(4)"
                  v-model="feedbackDetails"
                  variant="underlined"
                  density="compact"
                  hide-details
                  style="min-width: 180px"
                ></v-text-field>
              </div>
            </div>

            <v-btn
              v-if="selectedQuality && !feedbackSent"
              color="success"
              class="mt-3"
              :loading="feedbackLoading"
              @click="submitFeedback"
            >
              {{ t('feedbackSend') }}
            </v-btn>
          </v-col>

          <v-col cols="5" class="d-flex flex-column align-center">
            <img :src="require(`@/assets/disa-feedback.png`)" width="180" />
            <v-alert
              v-if="feedbackSent"
              type="success"
              variant="tonal"
              class="mt-2 w-100 text-center font-weight-bold"
            >
              {{ t('feedbackThankYou') }}
            </v-alert>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<i18n src="./locales.json"></i18n>

<style scoped>
.braille-loader {
  display: inline-block;
  overflow: hidden;
  white-space: nowrap;
  font-family: monospace;
  font-size: 1.5rem;
  line-height: 1;
  /* Smooth edge fading */
  -webkit-mask-image: linear-gradient(
    to right,
    transparent,
    black 20%,
    black 80%,
    transparent
  );
  mask-image: linear-gradient(
    to right,
    transparent,
    black 20%,
    black 80%,
    transparent
  );
}

/* Different widths for different vibes */
.wave-style {
  width: 15ch;
}
.dots-style {
  width: 4ch;
  font-size: 1rem;
}

.braille-loader span {
  display: inline-block;
  animation: scroll 1.2s linear infinite;
}

@keyframes scroll {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-50%);
  }
}

.feedback-quality-selected {
  border-bottom: 2px solid currentColor;
}
</style>
